import { SecretdGatewayClient } from './gateway'

/**
 * An Authcore Vault client. It communicates with Secretd through a gateway provided by Authcore.
 * Either `accessToken` or `staticKey` must be provided to authenticate the user.
 *
 * @public
 * @param {object} config
 * @param {string} config.apiBaseURL The base URL for the Authcore instance.
 * @param {string} config.accessToken Access token of an Authcore user.
 * @param {string} config.staticKey ECDH public key of a Secretd user.
 */
class AuthcoreVaultClient {
  constructor (config) {
    this.gateway = new SecretdGatewayClient(config)
  }

  /**
   * Generate a new HDWallet object with the given object id.
   *
   * Reference:
   *
   * [BIP-0032](https://github.com/bitcoin/bips/blob/master/bip-0032.mediawiki),
   * [BIP-0039](https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki) and
   * [BIP-0044](https://github.com/bitcoin/bips/blob/master/bip-0044.mediawiki).
   *
   * @param {string} oid ID of the object.
   * @param {number} [size] The size (in bytes) of the seed.
   * @returns {Promise<object>} Info of the generated object.
   */
  async hdwalletGenerate (oid, size) {
    if (!size) {
      size = 24
    }
    var params = [oid, size]
    var resp = await this.gateway.sendRequest('hdwallet_generate', params)
    return {
      oid: resp
    }
  }

  /**
   * Get the hierarchical determistic (HD) child public key with the given derivation path.
   *
   * @param {string} oid ID of the object.
   * @param {string} path Derivation path.
   * @returns {Promise<object>} The HD child public keys.
   */
  async hdwalletGetExtendedPublicKey (oid, path) {
    var params = [oid, path]
    var resp = await this.gateway.sendRequest('hdwallet_get_extended_public_key', params)
    if (!Array.isArray(resp) || resp.length !== 2) {
      throw new Error('invalid resposne')
    }
    return {
      publicKey: resp[0],
      extendedPublicKey: resp[1]
    }
  }

  /**
   * Signs an Ethereum transaction.
   *
   * @param {string} oid ID of the object.
   * @param {string} path Derivation path.
   * @param {string} data The payload to be signed in hex.
   * @returns {Promise<string>} The signature of the given payload in hex.
   */
  async hdwalletEthereumSign (oid, path, data) {
    var params = [oid, path, data]
    var resp = await this.gateway.sendRequest('hdwallet_ethereum_sign', params)
    return resp
  }

  /**
   * Signs a Cosmos transaction.
   *
   * @param {string} oid ID of the object.
   * @param {string} path Derivation path.
   * @param {string} data The payload to be signed in hex.
   * @returns {Promise<string>} The signature of the given payload in hex.
   */
  async hdwalletCosmosSign (oid, path, data) {
    var params = [oid, path, data]
    var resp = await this.gateway.sendRequest('hdwallet_secp256k1_sign', params)
    return resp
  }

  /**
   * Export the seed mnemonic.
   * Requires the access token from Authcore have export key capability.
   *
   * @param {string} oid ID of the object.
   * @returns {Promise<string>} The seed mnemonic of the object.
   */
  async hdwalletExportMnemonic (oid) {
    var params = [oid]
    var resp = await this.gateway.sendRequest('hdwallet_export_mnemonic', params)
    return resp
  }

  /**
   * Export the private key of the path-derived key.
   * Requires the access token from Authcore have export key capability.
   *
   * @param {string} oid ID of the object.
   * @param {string} path Derivation path.
   * @returns {Promise<string>} The seed mnemonic of the object.
   */
  async hdwalletExportKey (oid, path) {
    var params = [oid, path]
    var resp = await this.gateway.sendRequest('hdwallet_export_key', params)
    return resp
  }

  /**
   * Lookup an Authcore user by public user ID.
   *
   * @param {string} authcoreUserId ID of the Authcore User.
   * @returns {Promise<string>} Secretd UID of the user, or null if the user has not been
   * registered by Secretd.
   */
  async authcoreLookupUser (authcoreUserId) {
    var params = [authcoreUserId]
    var resp = await this.gateway.sendRequest('authcore_lookup_user', params)
    return resp
  }

  /**
   * Lookup an Authcore user by public user ID or create if it doesn't exist.
   *
   * @param {string} authcoreUserId ID of the Authcore User.
   * @returns {Promise<string>} Secretd UID of the user.
   */
  async authcoreLookupOrCreateUser (authcoreUserId) {
    var params = [authcoreUserId]
    var resp = await this.gateway.sendRequest('authcore_lookup_or_create_user', params)
    return resp
  }
}

export { AuthcoreVaultClient }
